import React from 'react';

import {
  TeamGiveItemAsync,
  TeamGiveTitleAsync,
  TeamRemoveItemAsync,
  TeamRemoveTitleAsync,
  GiveScore as TeamGiveScore,
  RemoveScore as TeamRemoveScore
} from '../../utils/game-engine/team';
import {
  UpdateTeamStateAsync,
  UpdatePlayerStateAsync
} from '../../utils/game-engine/base';
import {
  ShowTask,
  HideTask,
  ShowHideZoneArea
} from '../../utils/game-engine/player';
import { TeamContext } from '../../contexts/team';
import { GameContext } from '../../contexts/game';
import { PlayerContext } from '../../contexts/player';
import { useTimers } from '../../hooks/use-game-timer';
import ListenerManager from '../../utils/listener-manager';
import { useGameToast } from '../../hooks/use-game-toast';

export const Team: React.FC = () => {
  const [, setPlayer] = React.useContext(PlayerContext);
  const [team, setTeam] = React.useContext(TeamContext);
  const [, , hubConnection] = React.useContext(GameContext);

  const teamState = team.teamState;
  const teamCode = teamState?.code;

  const timers = useTimers();

  const {
    addItemToast,
    addScoreToast,
    addTitleToast,
    removeItemToast,
    removeScoreToast,
    removeTitleToast
  } = useGameToast();

  const addRemoveItemHandler = (action: 'add' | 'remove', message: any) => {
    setTeam((prev) => {
      const response =
        action === 'add'
          ? TeamGiveItemAsync(prev, message.id, parseInt(message.quantity))
          : TeamRemoveItemAsync(prev, message.id, parseInt(message.quantity));
      return UpdateTeamStateAsync(prev, response);
    });

    if (action === 'add') addItemToast(message.id, message.quantity, true);
    if (action === 'remove')
      removeItemToast(message?.id, message?.quantity, true);
  };

  const addRemoveTitleHandler = (action: 'add' | 'remove', message: any) => {
    setTeam((prev) => {
      const response =
        action === 'add'
          ? TeamGiveTitleAsync(prev.teamState!, message.id)
          : TeamRemoveTitleAsync(prev.teamState!, message?.id);
      return UpdateTeamStateAsync(prev, response);
    });

    if (action === 'add') addTitleToast(message?.id, true);
    if (action === 'remove') removeTitleToast(message?.id, true);
  };

  const addRemoveScoreHandler = (action: 'add' | 'remove', message: any) => {
    setTeam((prev) => {
      const response =
        action === 'add'
          ? TeamGiveScore(prev.teamState!, parseInt(message?.quantity))
          : TeamRemoveScore(prev.teamState!, parseInt(message?.quantity));
      return UpdateTeamStateAsync(prev, response);
    });

    if (action === 'add' && parseInt(message.quantity) !== 0)
      addScoreToast(message?.quantity, true);
    if (action === 'remove' && parseInt(message.quantity) !== 0)
      removeScoreToast(message?.quantity, true);
  };

  const teamStateFullHandler = async (message: any) => {
    if (message && message.teamStateFull) {
      setTeam((prev) => UpdateTeamStateAsync(prev, message.teamStateFull));
    }
  };

  const enabledChatHandler = (isChatDisabled: boolean) => {
    setTeam((prev) =>
      UpdateTeamStateAsync(prev, {
        ...prev.teamState!,
        isChatDisabled
      })
    );
  };

  const showHideObjectHandler = async (
    action: 'showTask' | 'hideTask' | 'showArea' | 'hideArea',
    message: any
  ) => {
    setPlayer((prev) => {
      if (prev.playerState) {
        let response = prev.playerState;

        if (action === 'showTask') response = ShowTask(prev, message?.id!);
        if (action === 'hideTask') response = HideTask(prev, message?.id!);
        if (action === 'showArea')
          response = ShowHideZoneArea(prev, message?.id!, true);
        if (action === 'hideArea')
          response = ShowHideZoneArea(prev, message?.id!, false);

        return UpdatePlayerStateAsync(prev, response);
      }

      return prev;
    });
  };

  const timeHandler = async (
    action: 'start' | 'stop' | 'pause' | 'resume',
    message: any
  ) => {
    if (!message || !message.id) return;
    const timerId = message.id;

    if (action === 'start' || action === 'resume') timers.start(timerId);
    if (action === 'pause') timers.pause(timerId);
    if (action === 'stop') timers.stop(timerId);
  };

  React.useEffect(() => {
    if (!hubConnection || !teamCode) return;

    const handler = (content: any) => {
      new ListenerManager({
        TeamStateFull: teamStateFullHandler,
        TeamEnabledChat: () => enabledChatHandler(true),
        TeamDisabledChat: () => enabledChatHandler(false),
        TeamStopTimer: (message) => timeHandler('stop', message),
        TeamStartTimer: (message) => timeHandler('start', message),
        TeamPauseTimer: (message) => timeHandler('pause', message),
        TeamResumeTimer: (message) => timeHandler('resume', message),
        TeamAddItem: (message) => addRemoveItemHandler('add', message),
        TeamAddScore: (message) => addRemoveScoreHandler('add', message),
        TeamAddTitle: (message) => addRemoveTitleHandler('add', message),
        TeamRemoveItem: (message) => addRemoveItemHandler('remove', message),
        TeamShowTask: (message) => showHideObjectHandler('showTask', message),
        TeamHideTask: (message) => showHideObjectHandler('hideTask', message),
        TeamShowArea: (message) => showHideObjectHandler('showArea', message),
        TeamHideArea: (message) => showHideObjectHandler('hideArea', message),
        TeamRemoveTitle: (message) => addRemoveTitleHandler('remove', message),
        TeamRemoveScore: (message) => addRemoveScoreHandler('remove', message)
      }).listen(content);
    };

    hubConnection.on(teamCode, handler);

    return () => hubConnection.off(teamCode, handler);
  }, [hubConnection, teamCode]);

  return <></>;
};
